import { formatGel } from '@/utils/money';
import type {
    OperationAmountSource,
    OperationLineDraft,
    OperationLineSide,
} from '@/api/models';

/**
 * The editor's live posting preview: what a recipe would book for a sample
 * amount.
 *
 * ## Why this arithmetic exists twice
 *
 * `LineAmountTetri` in `BosApi/Utilities/OperationPreview.cs` is the same sum in
 * C#. Two implementations of one calculation is a duplication that would
 * normally be a defect, and it is accepted here because the alternative — a
 * preview endpoint called on every keystroke in an amount field — is worse.
 *
 * The risk is contained the only way it can be: **both sides run the same
 * committed fixture.** `src/tests/api/operations/operationPreviewCases.json` and
 * `BosApi/BosApi.Tests/Fixtures/operation-preview-cases.json` are byte-for-byte
 * identical, and each repo's test suite runs its own copy. Change one
 * implementation and you must change the other, and the fixture with them —
 * whichever side drifts fails its own test.
 *
 * ## The money contract
 *
 * Integer tetri throughout; a float never touches an amount. `parseGelToTetri`
 * is what turns the sample-amount field into the number this takes, and
 * `formatGel` is what renders the result — the two edges in `@/utils/money`, as
 * everywhere else in this folder.
 */

/** Basis points per whole unit: 10 000 bps = 100%. */
const BASIS_POINTS = 10_000;

/**
 * The VAT inside a VAT-inclusive amount.
 *
 * **Derived by rounding the net and subtracting**, rather than by rounding the
 * tax directly. Round both independently and they can land a tetri apart, and an
 * entry a tetri out does not post at all — so this is what makes
 * `net + vat === gross` hold exactly, at every amount.
 *
 * `Math.round` and C#'s `MidpointRounding.AwayFromZero` agree on non-negative
 * inputs, which is all this ever sees: an amount is entered unsigned, and a
 * negative is a parse error rather than a credit (`parseGelToTetri`).
 */
const vatWithin = (grossTetri: number, vatRateBps: number): number => {
    if (vatRateBps <= 0 || grossTetri === 0) {
        return 0;
    }
    const net = Math.round(
        (grossTetri * BASIS_POINTS) / (BASIS_POINTS + vatRateBps),
    );
    return grossTetri - net;
};

/**
 * The figure one line posts, in whole tetri.
 *
 * `USER_INPUT` has no second figure to type in a preview, so the sample amount
 * stands in for it and the caller labels the line as illustrative. Rendering it
 * as zero would show a preview that does not balance — the one thing the preview
 * exists to demonstrate.
 */
export const lineAmountTetri = (
    source: OperationAmountSource,
    amountTetri: number,
    vatRateBps: number,
): number => {
    switch (source) {
        case 'GROSS':
            return amountTetri;
        case 'NET':
            return amountTetri - vatWithin(amountTetri, vatRateBps);
        case 'VAT_OF_GROSS':
            return vatWithin(amountTetri, vatRateBps);
        default:
            return amountTetri;
    }
};

/** One rendered leg of the previewed entry. */
export type PreviewLine = {
    /** Stable across renders — pair group and side identify a line uniquely. */
    id: string;
    pairGroup: number;
    side: OperationLineSide;
    /** The account this posts to, or `null` when the line is a slot. */
    accountId: string | null;
    /** The slot's English label, for the dashed placeholder. `null` on a fixed line. */
    slotLabelEn: string | null;
    slotLabelKa: string | null;
    amountSource: OperationAmountSource;
    /** Debit amount in tetri; `0` on a credit line. */
    debitTetri: number;
    /** Credit amount in tetri; `0` on a debit line. */
    creditTetri: number;
};

export type PreviewResult = {
    lines: PreviewLine[];
    totalDebitTetri: number;
    totalCreditTetri: number;
    /**
     * What the recipe's VAT lines came to, in tetri — `0` when it has none.
     *
     * Summed off the **debit** side only, because a VAT pair posts the same
     * figure to both and adding them would double it. It exists so the panel can
     * say "the VAT line computes ₾18.00 of the ₾118.00 gross": the totals prove
     * the entry balances, but a VAT line derived from the wrong rate balances
     * just as neatly as one derived from the right one, and this is the figure
     * that gives it away.
     */
    vatTetri: number;
    /** The sample the recipe was run against, echoed so the panel can quote it. */
    grossTetri: number;
    /**
     * Whether the two totals agree.
     *
     * It is always `true` for a recipe the server would accept, because both
     * lines of a pair are required to share an amount source — so a pair
     * balances by construction. It is computed and rendered anyway, because a
     * half-built recipe in the editor is exactly the state where it is `false`,
     * and that is the moment the author needs to see it.
     */
    balanced: boolean;
};

/**
 * Run a recipe against a sample amount.
 *
 * Pure, and deliberately tolerant: it previews whatever the editor currently
 * holds, including a pair that is missing its credit side. Refusing to render
 * an incomplete recipe would blank the preview at every intermediate state of
 * building one, which is most of the time the author spends here.
 */
export const previewOperation = (
    lines: readonly OperationLineDraft[],
    amountTetri: number,
    vatRateBps: number,
): PreviewResult => {
    const previewed: PreviewLine[] = lines
        .map((line) => {
            const amount = lineAmountTetri(
                line.amountSource,
                amountTetri,
                vatRateBps,
            );
            return {
                id: `${line.pairGroup}-${line.side}`,
                pairGroup: line.pairGroup,
                side: line.side,
                accountId: line.accountId,
                slotLabelEn: line.slotLabelEn,
                slotLabelKa: line.slotLabelKa,
                amountSource: line.amountSource,
                debitTetri: line.side === 'DEBIT' ? amount : 0,
                creditTetri: line.side === 'CREDIT' ? amount : 0,
            };
        })
        // The order an entry is written in: pair by pair, debit before credit.
        .sort(
            (a, b) =>
                a.pairGroup - b.pairGroup ||
                (a.side === 'DEBIT' ? 0 : 1) - (b.side === 'DEBIT' ? 0 : 1),
        );

    const totalDebitTetri = previewed.reduce(
        (sum, line) => sum + line.debitTetri,
        0,
    );
    const totalCreditTetri = previewed.reduce(
        (sum, line) => sum + line.creditTetri,
        0,
    );

    const vatTetri = previewed.reduce(
        (sum, line) =>
            line.amountSource === 'VAT_OF_GROSS' ? sum + line.debitTetri : sum,
        0,
    );

    return {
        lines: previewed,
        totalDebitTetri,
        totalCreditTetri,
        vatTetri,
        grossTetri: amountTetri,
        balanced: totalDebitTetri === totalCreditTetri,
    };
};

/**
 * Which sentence the balance strip shows, as a key and its parameters.
 *
 * Three outcomes, and the VAT clause is the reason this is not a boolean. A
 * recipe with a VAT line gets its computed tax quoted against the gross,
 * because a VAT line derived from the wrong rate balances exactly as neatly as
 * one derived from the right one — the totals cannot catch it, and this figure
 * can. A recipe without one has no such figure to quote and says so by not
 * quoting it, rather than by quoting a zero.
 */
export type BalanceMessage = {
    key: string;
    params: Record<string, string>;
};

export const balanceMessage = (preview: PreviewResult): BalanceMessage => {
    if (!preview.balanced) {
        return { key: 'operations.editor.preview.unbalanced', params: {} };
    }
    if (preview.vatTetri > 0) {
        return {
            key: 'operations.editor.preview.balancedVat',
            params: {
                vat: formatGel(preview.vatTetri),
                gross: formatGel(preview.grossTetri),
            },
        };
    }
    return { key: 'operations.editor.preview.balanced', params: {} };
};
